import React, { useState } from 'react';
import { X, Edit, Save, Clock, CheckCircle, DollarSign, User, Phone, FileText } from 'lucide-react';
import { RestaurantOrder } from '../types';
import { apiService } from '../services/api';

interface OrderDetailsModalProps {
  order: RestaurantOrder;
  onClose: () => void;
  onUpdate: (orderId: number, updates: any) => void;
}

const OrderDetailsModal: React.FC<OrderDetailsModalProps> = ({ order, onClose, onUpdate }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [orderStatus, setOrderStatus] = useState(order.status);
  const [paymentStatus, setPaymentStatus] = useState(order.paymentStatus);

  const statusOptions = [
    { value: 'pending', label: 'Pending', color: 'bg-yellow-100 text-yellow-800' },
    { value: 'confirmed', label: 'Confirmed', color: 'bg-blue-100 text-blue-800' },
    { value: 'preparing', label: 'Preparing', color: 'bg-orange-100 text-orange-800' },
    { value: 'ready', label: 'Ready', color: 'bg-green-100 text-green-800' },
    { value: 'delivered', label: 'Delivered', color: 'bg-green-100 text-green-800' },
    { value: 'cancelled', label: 'Cancelled', color: 'bg-red-100 text-red-800' },
  ];

  const paymentStatusOptions = [
    { value: 'pending', label: 'Pending', color: 'bg-yellow-100 text-yellow-800' },
    { value: 'paid', label: 'Paid', color: 'bg-green-100 text-green-800' },
    { value: 'refunded', label: 'Refunded', color: 'bg-red-100 text-red-800' },
  ];

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
    }).format(amount);
  };

  const formatDateTime = (dateTime: string) => {
    return new Date(dateTime).toLocaleString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleStatusUpdate = async (type: 'order' | 'payment', value: string) => {
    setIsUpdating(true);
    try {
      if (type === 'order') {
        await apiService.updateOrderStatus(order.id, { status: value as any });
        setOrderStatus(value as any);
      } else {
        await apiService.updatePaymentStatus(order.id, { paymentStatus: value as any });
        setPaymentStatus(value as any);
      }
      await onUpdate(order.id, {});
    } catch (error) {
      console.error('Error updating status:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  const getStatusColor = (status: string, type: 'order' | 'payment') => {
    if (type === 'order') {
      const option = statusOptions.find(opt => opt.value === status);
      return option?.color || 'bg-gray-100 text-gray-800';
    } else {
      const option = paymentStatusOptions.find(opt => opt.value === status);
      return option?.color || 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <>
      <style>
        {`
          body {
            overflow: hidden;
            margin: 0;
            padding: 0;
          }
        `}
      </style>
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4"
        style={{ 
          top: 0, 
          left: 0, 
          right: 0, 
          bottom: 0, 
          margin: 0, 
          padding: '1rem',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          position: 'fixed',
          width: '100vw',
          height: '100vh',
          zIndex: 9999
        }}
      >
        <div 
          className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col" 
          style={{ 
            margin: 0,
            backgroundColor: 'white',
            borderRadius: '1rem'
          }}
        >
        {/* Header - Integrated into content */}
        <div className="flex items-center justify-between p-6 bg-primary-500 text-white">
          <div>
            <h2 className="text-2xl font-bold">Order Details</h2>
            <p className="text-primary-100">Order #{order.id}</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setIsEditing(!isEditing)}
              className="flex items-center gap-2 px-4 py-2 text-white border border-white rounded-lg hover:bg-white hover:bg-opacity-20 transition-colors"
            >
              {isEditing ? <Save className="w-4 h-4" /> : <Edit className="w-4 h-4" />}
              {isEditing ? 'Save Changes' : 'Edit'}
            </button>
            <button
              onClick={onClose}
              className="p-2 text-white hover:text-primary-100 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-6" style={{ maxHeight: 'calc(90vh - 120px)' }}>
          {/* Order Status and Payment Status */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-800">Order Status</h3>
              {isEditing ? (
                <select
                  value={orderStatus}
                  onChange={(e) => handleStatusUpdate('order', e.target.value)}
                  disabled={isUpdating}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  {statusOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              ) : (
                <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${getStatusColor(orderStatus, 'order')}`}>
                  {statusOptions.find(opt => opt.value === orderStatus)?.label}
                </span>
              )}
            </div>

            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-800">Payment Status</h3>
              {isEditing ? (
                <select
                  value={paymentStatus}
                  onChange={(e) => handleStatusUpdate('payment', e.target.value)}
                  disabled={isUpdating}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  {paymentStatusOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              ) : (
                <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${getStatusColor(paymentStatus, 'payment')}`}>
                  {paymentStatusOptions.find(opt => opt.value === paymentStatus)?.label}
                </span>
              )}
            </div>
          </div>

          {/* Customer Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-800">Customer Information</h3>
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center gap-3">
                  <User className="w-5 h-5 text-gray-500" />
                  <div>
                    <p className="text-sm text-gray-600">Customer Name</p>
                    <p className="font-medium text-gray-900">{order.customerName}</p>
                  </div>
                </div>
                {order.customerPhone && (
                  <div className="flex items-center gap-3">
                    <Phone className="w-5 h-5 text-gray-500" />
                    <div>
                      <p className="text-sm text-gray-600">Phone Number</p>
                      <p className="font-medium text-gray-900">{order.customerPhone}</p>
                    </div>
                  </div>
                )}
              </div>
              {order.notes && (
                <div className="mt-4 flex items-start gap-3">
                  <FileText className="w-5 h-5 text-gray-500 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-600">Notes</p>
                    <p className="text-gray-900">{order.notes}</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Order Items */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-800">Order Items</h3>
            <div className="space-y-3">
              {order.items && order.items.length > 0 ? (
                order.items.map((item, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-medium text-gray-900">{item.name}</h4>
                      <span className="font-semibold text-primary-500">
                        {formatCurrency(item.totalPrice)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-sm text-gray-600">
                      <span>Qty: {item.quantity} × {formatCurrency(item.unitPrice)}</span>
                      <span>Total: {formatCurrency(item.quantity * item.unitPrice)}</span>
                    </div>
                    {item.notes && (
                      <p className="mt-2 text-sm text-gray-600 italic">Note: {item.notes}</p>
                    )}
                  </div>
                ))
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <p>No items found for this order</p>
                </div>
              )}
            </div>
          </div>

          {/* Order Summary */}
          <div className="bg-primary-50 rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-primary-500" />
                <h3 className="text-lg font-semibold text-primary-800">Order Summary</h3>
              </div>
              <span className="text-2xl font-bold text-primary-600">
                {formatCurrency(order.totalAmount)}
              </span>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-gray-500" />
                <div>
                  <p className="text-gray-600">Order Time</p>
                  <p className="font-medium text-gray-900">{formatDateTime(order.orderTime)}</p>
                </div>
              </div>
              {order.deliveryTime && (
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-gray-500" />
                  <div>
                    <p className="text-gray-600">Delivery Time</p>
                    <p className="font-medium text-gray-900">{formatDateTime(order.deliveryTime)}</p>
                  </div>
                </div>
              )}
              <div className="flex items-center gap-2">
                <span className="w-4 h-4 bg-primary-500 rounded-full"></span>
                <div>
                  <p className="text-gray-600">Items Count</p>
                  <p className="font-medium text-gray-900">{order.items.length} items</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        </div>
      </div>
    </>
  );
};

export default OrderDetailsModal;
